import type React from "react"
import { useState } from "react"
import { ClockCounterClockwise, TrendDown } from "@phosphor-icons/react"
import { toast } from "react-toastify"

import { Button, DatePicker } from "~/components/design-system/components"
import { HOVERABLE_TEXT } from "~/components/design-system/hoverable"
import { ConfirmationDialog } from "~/components/reusable/ConfirmationDialog"
import { Tooltip } from "~/components/reusable/Tooltip"
import { useSaveMetadata } from "~/hooks/useMetadata"
import { api } from "~/utils/api"
import { formatRelativeDate, startOfTodayUTC } from "~/utils/datetime"
import { activatedUntilForPhaseOut, lifecycleDateFieldCopy } from "~/utils/lifecycleUtils"
import { formatName } from "~/utils/namingUtils"

export type LifecycleDateField = keyof typeof lifecycleDateFieldCopy

const variantWord = (count: number) => (count === 1 ? "variant" : "variants")

const actionCopy = {
  phasingOutFrom: {
    Icon: TrendDown,
    label: "Phasing out",
    term: "phasing out",
    title: (count: number, term: React.ReactNode) => (
      <>
        Set {count} {variantWord(count)} to {term}
      </>
    ),
    confirmText: () => "Set to phasing out",
    pendingText: "Setting status...",
    success: (count: number) => `Set ${count} ${variantWord(count)} to phasing out`,
    dateField: lifecycleDateFieldCopy.phasingOutFrom,
  },
  activatedUntil: {
    Icon: ClockCounterClockwise,
    label: "Phase out",
    term: "Phase out",
    title: (count: number, term: React.ReactNode) => (
      <>
        {term} {count} {variantWord(count)}
      </>
    ),
    confirmText: (count: number) => `Phase out ${variantWord(count)}`,
    pendingText: "Phasing out...",
    success: (count: number) => `Phased out ${count} ${variantWord(count)}`,
    dateField: {
      label: "Phased out from",
      description:
        "From this week the product is Phased out - no demand and no suggested orders. Saved as Activated until the Sunday before.",
    },
  },
} satisfies Record<LifecycleDateField, unknown>

function effectDescription(field: LifecycleDateField, sellDownEnabled: boolean): string {
  if (field === "activatedUntil") {
    return "Phased-out variants stay in historical reports but aren't included in forecasts or planning. Use this for discontinued products."
  }
  if (sellDownEnabled) {
    return "Phasing-out variants keep a sell-down forecast until stockout but get no suggested purchases. Use this for products being sold down before discontinuation."
  }
  return "Phasing-out variants are excluded from forecasts and get no suggested purchases. Use this for products being sold down before discontinuation."
}

const payloadFor = (field: LifecycleDateField, date: Date) =>
  field === "phasingOutFrom"
    ? { phasingOutFrom: date, activatedUntil: null }
    : { activatedUntil: activatedUntilForPhaseOut(date) }

type Props = {
  variantIds: string[]
  field: LifecycleDateField
  trigger?: React.ReactNode
  disabled?: boolean
  disabledTooltip?: string
  onSuccess?: () => void
}

export function LifecycleConfirmationDialog({
  variantIds,
  field,
  trigger,
  disabled,
  disabledTooltip,
  onSuccess,
}: Props) {
  const [open, setOpen] = useState(false)
  const [date, setDate] = useState<Date>(startOfTodayUTC)
  const saveMetadata = useSaveMetadata({ showToast: false })

  const { data: skuIds } = api.sku.getSkuIdsForVariants.useQuery(
    { variantIds },
    { enabled: variantIds.length > 0 },
  )
  const { data: saleDates } = api.npi.variantSaleDates.useQuery(
    { variantId: variantIds[0] ?? "" },
    { enabled: open && variantIds.length === 1 },
  )
  const { data: company } = api.company.getCompany.useQuery()

  const count = variantIds.length
  const action = actionCopy[field]
  const fieldCopy = action.dateField
  const lastSaleDate = count === 1 ? saleDates?.mostRecentSaleDate : null
  const lastSaleSentence = lastSaleDate
    ? `Last ${formatName("sale")} was ${formatRelativeDate(lastSaleDate)}.`
    : ""

  const handleConfirm = () => {
    if (!skuIds || skuIds.length === 0) return
    saveMetadata.mutate(
      { skuIds, ...payloadFor(field, date) },
      {
        onSuccess: () => {
          toast.success(action.success(count))
          setOpen(false)
          onSuccess?.()
        },
      },
    )
  }

  return (
    <ConfirmationDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && saveMetadata.isPending) return
        if (next) setDate(startOfTodayUTC())
        setOpen(next)
      }}
      title={action.title(
        count,
        <Tooltip title={effectDescription(field, company?.phasingOutSellDownForecast ?? false)}>
          <span className={HOVERABLE_TEXT}>{action.term}</span>
        </Tooltip>,
      )}
      description={lastSaleSentence || undefined}
      onOpenAutoFocus={(e) => e.preventDefault()}
      confirmText={saveMetadata.isPending ? action.pendingText : action.confirmText(count)}
      onConfirm={handleConfirm}
      loading={saveMetadata.isPending}
      requires="edit"
      disabled={disabled}
      disabledTooltip={disabledTooltip}
      trigger={
        // No `disabled` here: the gated trigger owns it, and Radix slot merging would let a
        // child's own `disabled` shadow the one the gate injects.
        trigger ?? (
          <Button size="sm" variant="neutral">
            <action.Icon size={16} />
            {action.label}
          </Button>
        )
      }
    >
      <div className="flex flex-col gap-1.5">
        <span className="label-sm font-medium text-fg-base">{fieldCopy.label}</span>
        <p className="text-xs text-fg-subtle">{fieldCopy.description}</p>
        <DatePicker fullWidth value={date} onChange={(next) => next && setDate(next)} />
      </div>
    </ConfirmationDialog>
  )
}
